import type { ReactNode } from "react";
import { CircleHelp } from "lucide-react";
import { ClickPopover, PopoverHeading, StatusPopover, cx, formatDateTime, formatShortDate } from "@amber/ui";
import type { SourceHealthKey, SourceRow, TimelineMark } from "../../contracts/index";

export function HealthMark({ state }: { state: SourceHealthKey }) {
  const base = "inline-block size-2 flex-none";
  switch (state) {
    case "failing":
      return <span aria-hidden="true" className={cx(base, "rounded-[2px] bg-attention")} />;
    case "partial":
      return (
        <span
          aria-hidden="true"
          className={cx(
            base,
            "rounded-[2px] border-[1.5px] border-attention bg-[linear-gradient(90deg,var(--color-attention)_50%,transparent_50%)]",
          )}
        />
      );
    case "late":
      return (
        <span aria-hidden="true" className={cx(base, "rounded-full border-[1.5px] border-dashed border-waiting")} />
      );
    case "unusually_quiet":
      return <span aria-hidden="true" className={cx(base, "rounded-full border-[1.5px] border-fg")} />;
    case "healthy":
      return <span aria-hidden="true" className={cx(base, "rounded-full bg-done")} />;
    case "paused":
      return (
        <span aria-hidden="true" className="inline-flex h-2 w-2 flex-none gap-[2px]">
          <span className="h-2 w-[3px] bg-muted" />
          <span className="h-2 w-[3px] bg-muted" />
        </span>
      );
    default:
      return <span aria-hidden="true" className={cx(base, "rounded-full border-[1.5px] border-dotted border-muted")} />;
  }
}

const HEALTH_TEXT: Partial<Record<SourceHealthKey, string>> = {
  failing: "text-attention font-strong",
  partial: "text-attention font-strong",
  late: "text-waiting font-strong",
  unusually_quiet: "text-fg font-strong",
};

export function HealthLabel({ state, label }: { state: SourceHealthKey; label: string }) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-[7px] whitespace-nowrap",
        HEALTH_TEXT[state] ?? (state === "healthy" ? "text-fg" : "text-muted"),
      )}
    >
      <HealthMark state={state} />
      {label}
    </span>
  );
}

export function SourceHealthCell({ row }: { row: SourceRow }) {
  const { health } = row;
  if (!health.reason) return <HealthLabel state={health.state} label={health.label} />;
  return (
    <StatusPopover
      label={<HealthLabel state={health.state} label={health.label} />}
      accessibleLabel={`${health.label}: ${health.reason}`}
    >
      <PopoverHeading icon={<HealthMark state={health.state} />}>{health.label}</PopoverHeading>
      <p>{health.reason}</p>
      <dl>
        {row.last_scan ? (
          <>
            <dt>Last scan</dt>
            <dd>{row.last_scan.label}</dd>
          </>
        ) : null}
        {row.complete_through ? (
          <>
            <dt>Complete through</dt>
            <dd>{formatDateTime(row.complete_through)}</dd>
          </>
        ) : null}
        <dt>Next</dt>
        <dd>{nextScanText(row)}</dd>
        {row.usual_gap_days !== null && health.state === "unusually_quiet" ? (
          <>
            <dt>Usual gap</dt>
            <dd>{formatDays(row.usual_gap_days)} (median of the last 10 publications)</dd>
          </>
        ) : null}
        {row.pause ? (
          <>
            <dt>Paused by</dt>
            <dd>
              {row.pause.by}, {formatDateTime(row.pause.at)}
            </dd>
          </>
        ) : null}
      </dl>
      {health.platform_wide ? (
        <p className="mt-2 text-md text-muted">Part of a platform-wide cause; see the Platforms tab.</p>
      ) : null}
    </StatusPopover>
  );
}

export function formatDays(days: number): string {
  if (days < 1) return `${String(Math.max(1, Math.round(days * 24)))} h`;
  return `${String(Math.round(days))} day${Math.round(days) === 1 ? "" : "s"}`;
}

export function nextScanText(row: Pick<SourceRow, "next_scan">): string {
  const next = row.next_scan;
  switch (next.kind) {
    case "scanning":
      return "Scanning now";
    case "paused":
      return "Paused";
    case "none":
      return next.text;
    case "at": {
      const ms = new Date(next.at).getTime() - Date.now();
      if (ms <= 60_000) return next.late ? "Overdue" : "Within a minute";
      const minutes = Math.round(ms / 60_000);
      return minutes < 60 ? `In ${String(minutes)} min` : `In ${String(Math.round(minutes / 60))} h`;
    }
  }
}

const MARK_STYLE: Record<TimelineMark["kind"], string> = {
  complete: "bg-[color-mix(in_srgb,var(--color-link)_32%,var(--color-surface-3))]",
  read_not_complete:
    "bg-[repeating-linear-gradient(90deg,color-mix(in_srgb,var(--color-link)_40%,transparent)_0_3px,transparent_3px_5px)]",
  not_yet_scanned: "bg-[repeating-linear-gradient(135deg,var(--color-divider)_0_2px,transparent_2px_5px)]",
  paused: "bg-[repeating-linear-gradient(90deg,var(--color-muted)_0_2px,transparent_2px_4px)] opacity-60",
  not_monitored: "bg-transparent",
  gap: "bg-[repeating-linear-gradient(135deg,var(--color-attention)_0_2px,var(--color-attention-soft)_2px_5px)] border border-attention",
  accepted_gap:
    "bg-[repeating-linear-gradient(135deg,var(--color-muted)_0_2px,transparent_2px_5px)] border border-muted",
  extending:
    "bg-[repeating-linear-gradient(-45deg,color-mix(in_srgb,var(--color-link)_35%,transparent)_0_3px,transparent_3px_6px)] shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--color-link)_60%,transparent)]",
};

const BACK_TO_LINE = "absolute -inset-y-[3px] z-[1] -ml-px w-0.5 rounded-[1px] bg-link";

export function CoverageTimeline({
  row,
  from,
  to,
  large = false,
  onGap,
}: {
  row: Pick<SourceRow, "timeline" | "timeline_label">;
  from: string;
  to: string;
  large?: boolean;
  onGap?: (gapId: string) => void;
}) {
  const start = new Date(from).getTime();
  const span = Math.max(1, new Date(to).getTime() - start);
  if (row.timeline.length === 0) return <span className="text-md text-muted">{row.timeline_label}</span>;
  const pct = (iso: string) =>
    `${String(Math.min(100, Math.max(0, ((new Date(iso).getTime() - start) / span) * 100)))}%`;
  const width = (m: TimelineMark) =>
    `${String(Math.max(0, ((new Date(m.to).getTime() - new Date(m.from).getTime()) / span) * 100))}%`;
  return (
    <div role="group" aria-label={row.timeline_label} className={cx("relative w-full", large ? "h-6" : "h-3")}>
      <div className="absolute inset-y-0 right-0 left-0 rounded-[3px] bg-surface-2" />
      {row.timeline
        .filter((m) => m.kind !== "gap")
        .map((m) => (
          <span
            key={`${m.kind}-${m.from}`}
            className={cx("absolute inset-y-0 rounded-[2px]", MARK_STYLE[m.kind])}
            style={{ left: pct(m.from), width: width(m) }}
            title={m.label}
          />
        ))}
      {row.timeline
        .filter((m) => m.kind === "extending")
        .map((m) => (
          <span key={`back-to-${m.to}`} aria-hidden="true" className={BACK_TO_LINE} style={{ left: pct(m.to) }} />
        ))}
      {row.timeline
        .filter((m) => m.kind === "gap")
        .map((m) => (
          <span
            key={`${m.kind}-${m.from}`}
            className="absolute -inset-y-1.5 flex min-w-[24px] -translate-x-[10px] items-center justify-center"
            style={{ left: pct(m.from), width: width(m) }}
          >
            <ClickPopover
              label={m.label}
              className="flex h-full w-full items-center px-[10px] focus-visible:outline-2 focus-visible:outline-focus"
              trigger={
                <span
                  aria-hidden="true"
                  className={cx("block w-full min-w-1 rounded-[2px]", MARK_STYLE.gap, large ? "h-6" : "h-3")}
                />
              }
            >
              <PopoverHeading>Gap</PopoverHeading>
              <p>{m.label}</p>
              {onGap && m.gap_id ? (
                <button
                  type="button"
                  className="text-md text-link underline"
                  onClick={() => {
                    if (m.gap_id) onGap(m.gap_id);
                  }}
                >
                  Show this source&apos;s gaps
                </button>
              ) : null}
            </ClickPopover>
          </span>
        ))}
    </div>
  );
}

const KEY_MARKS: readonly { kind: TimelineMark["kind"]; label: string; text: string }[] = [
  { kind: "complete", label: "Complete", text: "Fully read up to “complete through”" },
  { kind: "gap", label: "Gap", text: "Not read. Hover, focus or tap it for the reason; a running backfill fills it" },
  { kind: "read_not_complete", label: "Read, not complete", text: "Newer pages read; waits for an earlier gap" },
  { kind: "not_yet_scanned", label: "Not yet scanned", text: "After “complete through”; striped while scanning" },
  { kind: "paused", label: "Paused", text: "No scans until resumed" },
  { kind: "not_monitored", label: "Not monitored", text: "Before monitoring started" },
  { kind: "accepted_gap", label: "Accepted gap", text: "Missing for a known reason; no more attempts" },
  {
    kind: "extending",
    label: "History being extended",
    text: "Reading further back; the line marks “complete back to”",
  },
];

function KeySwatch({ kind }: { kind: TimelineMark["kind"] }) {
  if (kind === "extending")
    return (
      <span aria-hidden="true" className="relative block h-2.5 w-full rounded-[3px] bg-surface-2">
        <span className={cx("absolute inset-y-0 left-0 w-[70%] rounded-[2px]", MARK_STYLE.extending)} />
        <span className={BACK_TO_LINE} style={{ left: "70%" }} />
        <span className={cx("absolute inset-y-0 right-0 w-[30%]", MARK_STYLE.complete)} />
      </span>
    );
  return (
    <span aria-hidden="true" className="relative block h-2.5 w-full rounded-[3px] bg-surface-2">
      <span
        className={cx(
          "absolute inset-0 rounded-[2px]",
          MARK_STYLE[kind],
          kind === "not_monitored" && "border border-dashed border-divider",
        )}
      />
    </span>
  );
}

export function CoverageKey() {
  return (
    <ClickPopover
      contentLabel="Coverage key"
      className="inline-flex h-7 min-w-7 items-center justify-center gap-1.5 rounded-lg border border-border bg-bg px-2 text-md text-muted hover:bg-surface-2 hover:text-fg aria-expanded:bg-surface-3 aria-expanded:text-fg"
      contentClassName="w-[300px]"
      trigger={
        <>
          <CircleHelp size={14} strokeWidth={1.8} aria-hidden="true" />
          <span>Key</span>
        </>
      }
    >
      <PopoverHeading>Coverage timeline</PopoverHeading>
      <ul className="m-0 list-none p-0">
        {KEY_MARKS.map((m) => (
          <li key={m.kind} className="grid grid-cols-[34px_1fr] items-center gap-2.5 py-1 text-md text-muted">
            <KeySwatch kind={m.kind} />
            <span>
              <b className="block font-strong text-fg">{m.label}</b>
              {m.text}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-sm text-muted">
        All rows share one time axis, so gaps on the same dates line up. A complete scan doesn&apos;t prove nothing was
        missed.
      </p>
    </ClickPopover>
  );
}

export function TimelineAxis({ from, to }: { from: string; to: string }) {
  const start = new Date(from).getTime();
  const end = new Date(to).getTime();
  const ticks: { at: number; label: string }[] = [];
  const cursor = new Date(start);
  cursor.setUTCDate(1);
  cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  while (cursor.getTime() < end - (end - start) * 0.08) {
    ticks.push({
      at: cursor.getTime(),
      label: cursor.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" }),
    });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return (
    <span aria-hidden="true" className="relative block h-3 w-full text-[10px] font-normal text-muted">
      {ticks.map((t) => (
        <span
          key={t.at}
          className="absolute -translate-x-1/2"
          style={{ left: `${String(((t.at - start) / (end - start)) * 100)}%` }}
        >
          {t.label}
        </span>
      ))}
      <span className="absolute right-0">now</span>
    </span>
  );
}

export function ActivityBars({ daily, label }: { daily: readonly number[]; label: string }) {
  const max = Math.max(1, ...daily);
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${String(daily.length * 4)} 24`}
      preserveAspectRatio="none"
      className="h-6 w-[108px] flex-none"
    >
      {daily.map((n, i) => {
        const h = n === 0 ? 1 : Math.max(2, (n / max) * 24);
        return (
          <rect
            key={i}
            x={i * 4}
            y={24 - h}
            width={3}
            height={h}
            className={
              i >= daily.length - 7
                ? "fill-link"
                : "fill-[color-mix(in_srgb,var(--color-link)_35%,var(--color-surface-3))]"
            }
          />
        );
      })}
    </svg>
  );
}

export function Monogram({ name, children }: { name: string; children?: ReactNode }) {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <span
      title={name}
      className="-ml-1 inline-grid size-[22px] flex-none place-items-center rounded-full border border-bg bg-surface-3 font-mono text-[9.5px] font-semibold text-muted first:ml-0"
    >
      {children ?? letters}
    </span>
  );
}

export function PlatformCode({ code }: { code: string }) {
  return (
    <span
      aria-hidden="true"
      className="inline-grid h-4 min-w-[22px] flex-none place-items-center rounded-[3px] bg-surface-3 px-1 font-mono text-[9.5px] font-semibold text-muted"
    >
      {code}
    </span>
  );
}

export function shortDate(iso: string | null): string {
  return iso ? formatShortDate(iso) : "—";
}
